import React, { useState, useEffect } from 'react';
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { 
  Flame, 
  Rocket, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  Target,
  ArrowRight,
} from 'lucide-react';

/**
 * System Health Badge
 * 
 * Compact badge showing system health status from the Operator Focus Engine.
 * Hover to see full details that would have been on the card.
 */
const SystemHealthBadge = ({ vertical = "all", className = "" }) => {
  const [focus, setFocus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // MA-PHASE11A-FINAL-RUNTIME-CLEANLINESS-GATE-022: Operator focus endpoint not implemented
    // Skip API call to prevent 500 errors in console
    setLoading(false);
    setError(null);
    setFocus(null);
    
    // TODO: Implement /operator/focus endpoint or remove this component
    return;
    
    /* DISABLED UNTIL ENDPOINT IMPLEMENTED
    const fetchFocus = async () => {
      try {
        setLoading(true);
        const verticalParam = vertical && vertical !== "all" ? vertical : "mortgage_protection";
        const res = await api.get(`/operator/focus?vertical=${verticalParam}`);
        setFocus(res.data);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load');
      } finally {
        setLoading(false);
      }
    };
    
    fetchFocus();
    */
  }, [vertical]);

  if (loading || error || !focus) {
    return null;
  }

  const isHealthy = focus.status === "healthy";
  const isCritical = focus.status === "critical";
  const isWarning = focus.status === "warning";

  const getMomentumIcon = () => {
    if (focus.momentum === "up") return <TrendingUp className="h-3 w-3 text-emerald-400" />;
    if (focus.momentum === "down") return <TrendingDown className="h-3 w-3 text-red-400" />;
    return <Minus className="h-3 w-3 text-slate-400" />;
  };

  const getMomentumLabel = () => {
    if (focus.momentum === "up") return "Trending Up";
    if (focus.momentum === "down") return "Trending Down";
    return "Stable";
  };

  const formatCurrency = (val) => {
    if (!val && val !== 0) return '—';
    const absVal = Math.abs(val);
    const sign = val < 0 ? '-' : '+';
    if (absVal >= 1000) return `${sign}$${(absVal / 1000).toFixed(1)}k`;
    return `${sign}$${absVal.toFixed(0)}`;
  };

  // Badge styling based on status
  const getBadgeClass = () => {
    if (isCritical) return "bg-red-500/20 text-red-300 border-red-500/30 hover:bg-red-500/30";
    if (isWarning) return "bg-amber-500/20 text-amber-300 border-amber-500/30 hover:bg-amber-500/30";
    return "bg-emerald-500/20 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/30";
  };

  const getStatusLabel = () => {
    if (isCritical) return "Critical";
    if (isWarning) return "Warning";
    return "System Healthy";
  };

  const getStatusIcon = () => {
    if (isCritical || isWarning) {
      return <Flame className="h-3 w-3" />;
    }
    return <Rocket className="h-3 w-3" />;
  };

  return (
    <HoverCard openDelay={100} closeDelay={100}>
      <HoverCardTrigger asChild>
        <Badge
          variant="outline"
          className={`cursor-default transition-colors ${getBadgeClass()} ${className}`}
          data-testid="system-health-badge"
        >
          <div className="flex items-center gap-1.5">
            {getStatusIcon()}
            <span className="text-xs">{getStatusLabel()}</span>
          </div>
        </Badge>
      </HoverCardTrigger>
      <HoverCardContent 
        side="bottom" 
        align="end"
        className="w-80 bg-slate-950 border-white/10 text-slate-100 p-4 z-50"
      >
        {isHealthy ? (
          // Healthy state content
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Rocket className="h-5 w-5 text-emerald-400" />
              <span className="font-semibold text-emerald-300">System Healthy</span>
            </div>
            <p className="text-sm text-slate-300 mb-3">
              All funnel stages within acceptable variance.
            </p>
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <ArrowRight className="h-4 w-4" />
              <span>Focus on scaling lead volume.</span>
            </div>
          </div>
        ) : (
          // Constraint state content
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Flame className={`h-5 w-5 ${isCritical ? 'text-red-400' : 'text-amber-400'}`} />
              <span className={`font-semibold ${isCritical ? 'text-red-300' : 'text-amber-300'}`}>
                Operator Focus
              </span>
              {isCritical && (
                <span className="px-2 py-0.5 text-xs bg-red-500/20 text-red-300 rounded-full">
                  Critical
                </span>
              )}
            </div>

            {/* Constraint Details */}
            <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
              <div>
                <div className="text-xs text-slate-500 mb-0.5">Primary Constraint</div>
                <div className="font-medium text-slate-200">
                  {focus.constraint_label || focus.constraint_stage}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-0.5">Variance</div>
                <div className={`font-medium ${focus.variance_percent < 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                  {focus.variance_percent}% vs Target
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-0.5">Revenue Impact</div>
                <div className={`font-medium ${focus.revenue_impact_monthly < 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                  {formatCurrency(focus.revenue_impact_monthly)} / month
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-0.5">Momentum</div>
                <div className="font-medium flex items-center gap-1">
                  {getMomentumIcon()}
                  <span className="text-slate-300">{getMomentumLabel()}</span>
                </div>
              </div>
            </div>

            {/* Action Today */}
            <div className="p-2 bg-black/30 rounded-lg border border-white/5">
              <div className="flex items-start gap-2">
                <Target className={`h-4 w-4 mt-0.5 flex-shrink-0 ${isCritical ? 'text-red-400' : 'text-amber-400'}`} />
                <div>
                  <div className="text-xs text-slate-500 mb-0.5">Action Today</div>
                  <div className="text-sm text-slate-200">{focus.action}</div>
                </div>
              </div>
            </div>

            {/* Current vs Target */}
            {focus.current_value !== null && (
              <div className="flex items-center gap-4 mt-3 pt-2 border-t border-white/10 text-xs">
                <span className="text-slate-500">
                  Current: <span className="text-slate-300">{focus.current_value}%</span>
                </span>
                <span className="text-slate-500">
                  Target: <span className="text-slate-300">{focus.benchmark_target}%</span>
                </span>
              </div>
            )}
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  );
};

export default SystemHealthBadge;
